import { useState } from "react";
import { auth } from "./firebase";

const SUBSCRIPTION_OPS_URL =
  process.env.REACT_APP_SUBSCRIPTION_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/subscriptionOps";
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_live_SH9JkBAqIshVls";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window?.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function SubscriptionCheckoutButton({
  planCode,
  serviceId = "",
  buttonLabel = "Activate Plan",
  disabled = false,
  onSuccess,
  onError,
}) {
  const [processing, setProcessing] = useState(false);
  const [lastError, setLastError] = useState("");

  const handleCheckout = async () => {
    if (processing || disabled) return;
    try {
      setProcessing(true);
      setLastError("");

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("Please login before purchasing a subscription.");
      }
      const token = await currentUser.getIdToken();

      const createRes = await fetch(SUBSCRIPTION_OPS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "create_order",
          planCode,
          serviceId,
        }),
      });
      const createData = await createRes.json().catch(() => ({}));
      if (!createRes.ok || !createData?.success) {
        throw new Error(createData?.error || `Subscription order failed (${createRes.status})`);
      }

      const order = createData.order || {};
      const plan = createData.plan || {};
      if (!order?.id || !order?.amount) {
        throw new Error("Invalid subscription order response.");
      }

      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded || !window?.Razorpay) {
        throw new Error("Razorpay SDK failed to load.");
      }

      const rzp = new window.Razorpay({
        key: order.key_id || RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency || plan.currency || "INR",
        name: "Petaar",
        description: plan.planName || "Petaar Subscription",
        order_id: order.id,
        prefill: {
          email: currentUser.email || "",
        },
        theme: { color: "#1e40af" },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(SUBSCRIPTION_OPS_URL, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
              },
              body: JSON.stringify({
                action: "verify_payment",
                ...response,
              }),
            });
            const verifyData = await verifyRes.json().catch(() => ({}));
            if (!verifyRes.ok || !verifyData?.success) {
              throw new Error(verifyData?.error || `Subscription verify failed (${verifyRes.status})`);
            }

            alert("✅ Subscription activated successfully.");
            if (typeof onSuccess === "function") {
              onSuccess(verifyData);
            }
          } catch (error) {
            setLastError(error.message || "Subscription verification failed.");
            if (typeof onError === "function") {
              onError(error);
            }
            alert(`❌ ${error.message || "Subscription verification failed."}`);
          } finally {
            setProcessing(false);
          }
        },
      });

      rzp.on("payment.failed", (failedEvent) => {
        const reason = failedEvent?.error?.description || failedEvent?.error?.reason || "Payment failed";
        setLastError(reason);
        if (typeof onError === "function") {
          onError(new Error(reason));
        }
        alert(`❌ Payment failed: ${reason}`);
        setProcessing(false);
      });

      rzp.on("modal.closed", () => {
        setProcessing(false);
      });

      rzp.open();
    } catch (error) {
      setLastError(error.message || "Subscription checkout failed.");
      if (typeof onError === "function") {
        onError(error);
      }
      alert(`❌ ${error.message || "Subscription checkout failed."}`);
      setProcessing(false);
    }
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <button onClick={handleCheckout} disabled={processing || disabled}>
        {processing ? "Processing..." : buttonLabel}
      </button>
      {lastError ? (
        <small style={{ color: "#b91c1c", display: "block", flexBasis: "100%" }}>
          Last subscription error: {lastError}
        </small>
      ) : null}
    </div>
  );
}
